<template>
  <div class="profile-overlay">
    <div class="profile-container">
      <div class="profile-left">
        <div class="profile-left-inner">
          <div class="profile-username" :class="{ 'editing': editingUsername }">
            <template v-if="!editingUsername">{{ userDisplayName }}</template>
            <template v-else>
              <input v-model="newUsername" class="username-input" />
            </template>
          </div>
          <a class="profile-link" v-if="!editingUsername" @click="editingUsername = true">change your username</a>
          <a class="profile-link" v-else @click="saveUsername">validate the username</a>
          <a class="profile-link" @click="logout">log out</a>
          <a class="profile-delete-link" @click="confirmDeleteAccount">delete your account</a>
          <div class="profile-streaming-label">new developments soon!</div>
          <div class="profile-support-buttons">
            <a href="https://patreon.com/NuanceRadio" target="_blank" rel="noopener" class="profile-support-btn patreon">Support on Patreon</a>
            <a href="https://www.paypal.com/donate/?hosted_button_id=SXGA8V28PV4LL" target="_blank" rel="noopener" class="profile-support-btn paypal">Donate via Paypal</a>
          </div>
        </div>
      </div>
      <div class="profile-right" :class="{ 'scrollable': favorites.length > 12 }">
        <div class="profile-title">your favorites tracks</div>
        <div v-if="loading" class="profile-loading">Loading your favorites...</div>
        <div v-else-if="!favorites.length" class="profile-empty">No favorites yet.</div>
        <div v-else class="profile-grid grid-pasttrack" :style="gridColumnsStyle">
          <last-track
            v-for="(track, idx) in favorites"
            :key="track.id || (track.title + '-' + idx)"
            :artist="track.artist"
            :title="track.title"
            :album="track.album"
            :year="track.year"
            :discogsUrl="track.discogs_url"
            :imageUrl="track.image_url"
            :favorited="true"
            @toggle-favorite="toggleFavorite(idx, track)"
            @open-signup="$emit('open-signup')"
          />
        </div>
      </div>
      <button class="profile-close" @click="$emit('close')">×</button>
    </div>
  </div>
</template>

<script>
import { auth } from '../firebase';
import { onAuthStateChanged, updateProfile, signOut, deleteUser } from 'firebase/auth';
import LastTrack from './last-track.vue';
import api from '../api';
import Vue from 'vue';
export default {
  name: 'ProfileOverlay',
  components: { LastTrack },
  data() {
    return {
      user: null,
      editingUsername: false,
      newUsername: '',
      favorites: [],
      loading: false
    }
  },
  computed: {
    userDisplayName() {
      if (!this.user) return '';
      return this.user.displayName || this.user.email || 'username';
    },
    gridColumnsStyle() {
      // Only apply on small screens
      if (window.innerWidth > 600) return {};
      const n = this.favorites.length;
      if (n === 1) {
        return { gridTemplateColumns: '1fr' };
      } else if (n === 2 || n === 3) {
        return { gridTemplateColumns: 'repeat(2, 1fr)' };
      }
      return {};
    }
  },
  created() {
    onAuthStateChanged(auth, user => {
      this.user = user;
      this.newUsername = user && user.displayName ? user.displayName : '';
      if (user) this.fetchFavorites();
    });
    // Listen for global favorites-updated event
    Vue.prototype.$eventBus = Vue.prototype.$eventBus || new Vue();
    this.$eventBus = Vue.prototype.$eventBus;
    this.$eventBus.$on('favorites-updated', this.fetchFavorites);
  },
  beforeDestroy() {
    if (this.$eventBus) {
      this.$eventBus.$off('favorites-updated', this.fetchFavorites);
    }
  },
  methods: {
    async fetchFavorites() {
      if (!this.user) return;
      this.loading = true;
      try {
        const res = await api.get(`/favorites/${this.user.uid}`);
        this.favorites = res.data || [];
      } catch (e) {
        this.favorites = [];
      }
      this.loading = false;
    },
    async saveUsername() {
      if (!this.newUsername || !this.user) return;
      await updateProfile(this.user, { displayName: this.newUsername });
      this.editingUsername = false;
      this.user.displayName = this.newUsername;
    },
    async logout() {
      await signOut(auth);
      this.$emit('close');
    },
    async toggleFavorite(idx, track) {
      // Un-favoriting removes the track from the grid and backend
      if (!this.user) return;
      try {
        await api.delete(`/favorites/${this.user.uid}`, { data: { id: track.id, artist: track.artist, title: track.title } });
        this.$delete(this.favorites, idx);
      } catch (e) {}
    },
    async confirmDeleteAccount() {
      if (!window.confirm('Are you sure you want to delete your account and all associated data? This action cannot be undone.')) return;
      await this.deleteAccount();
    },
    async deleteAccount() {
      if (!this.user) return;
      try {
        // Delete all favorites from backend
        await api.delete(`/favorites/${this.user.uid}`, { data: { deleteAll: true } });
      } catch (e) {}
      try {
        // Delete user from Firebase
        await deleteUser(this.user);
      } catch (e) {
        alert('Error deleting account. You may need to re-authenticate.');
        return;
      }
      this.$emit('close');
    }
  },
  watch: {
    user(newUser) {
      if (newUser) this.fetchFavorites();
    }
  }
};
</script>

<style scoped>
.profile-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 20000;
  background: rgba(255,255,255,0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
}
.profile-container {
  display: flex;
  flex-direction: row;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 2px 24px rgba(0,0,0,0.12);
  padding: 0;
  width: 80vw;
  max-width: 1100px;
  min-width: 320px;
  height: auto;
  min-height: 400px;
  position: relative;
  transition: width 0.2s, height 0.2s;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}
.profile-left {
  background: #f5f5f5;
  width: 28%;
  min-width: 180px;
  max-width: 340px;
  flex-basis: 28%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
  transition: width 0.2s;
}
.profile-left-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  min-height: 500px;
}
.profile-username {
  font-size: clamp(1.1rem, 2vw, 1.7rem);
  font-family: Syne, sans-serif;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.2rem;
  text-align: center;
}
.profile-username.editing {
  color: #888;
}
.username-input {
  font-size: clamp(1.1rem, 2vw, 1.7rem);
  font-family: Syne, sans-serif;
  font-weight: 700;
  color: #222;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 0.2rem;
  text-align: center;
  width: 90%;
  margin-bottom: 1.2rem;
}
.profile-link {
  color: #222;
  font-size: clamp(0.95rem, 1.2vw, 1.05rem);
  text-decoration: underline;
  margin-bottom: 0.7rem;
  cursor: pointer;
  font-family: Syne, sans-serif;
}
.profile-delete-link {
  color: #888;
  font-size: clamp(0.7rem, 1vw, 0.8rem);
  text-decoration: underline;
  margin-bottom: 0.7rem;
  cursor: pointer;
  font-family: Syne, sans-serif;
  display: block;
  margin-top: -0.2rem;
}
.profile-streaming-label {
  margin-top: 2.5rem;
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  color: #222;
  font-family: Syne, sans-serif;
  margin-bottom: 1.2rem;
  text-align: center;
}
.profile-streaming-icons {
  display: flex;
  flex-direction: row;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
  justify-content: center;
  width: 100%;
}
.profile-circle {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-streaming-icon-black {
  width: 32px;
  height: 32px;
  filter: brightness(0) invert(1);
}
.profile-right {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  padding: 2.5vw 2vw 2vw 2vw;
  min-width: 0;
  min-height: 0;
  max-height: 80vh;
  overflow: hidden;
  transition: padding 0.2s;
}
.profile-right.scrollable {
  overflow-y: auto;
  max-height: 600px;
}
.profile-title {
  font-size: clamp(1.1rem, 2vw, 1.6rem);
  font-family: Syne, sans-serif;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #222;
  text-align: center;
}
.profile-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-gap: 8px;
  padding: 4px;
  align-self: center;
  justify-content: center;
  background-color: #fff;
}
.profile-loading, .profile-empty {
  color: #888;
  font-size: 1.1rem;
  text-align: center;
  margin-top: 2rem;
}
.profile-close {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: transparent;
  border: 2px solid #222;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1.1rem;
  color: #222;
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-support-buttons {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.2rem;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
}
.profile-support-btn {
  font-family: Syne, sans-serif;
  font-size: clamp(1rem, 1.2vw, 1.08rem);
  font-weight: 600;
  padding: 0.7rem 1.4rem;
  border: none;
  text-decoration: none;
  color: #fff;
  background: #222;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 0.5rem;
  text-align: center;
  display: inline-block;
}
.profile-support-btn.patreon {
  background: #424242;
}
.profile-support-btn.paypal {
  background: #0070ba;
}
.profile-support-btn:hover {
  filter: brightness(1.1);
  color: #fff;
}
@media (max-width: 991px) {
  .profile-overlay {
    align-items: flex-start;
    justify-content: flex-start;
    justify-content: center;
  }
  .profile-container {
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 98vw;
    height: auto;
    max-height: 100vh;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
    border-radius: 0;
    overflow: auto;
    box-sizing: border-box;
    transition: width 0.2s, height 0.2s;
  }
  .profile-left {
    width: 100%;
    min-width: 0;
    max-width: 100vw;
    border-radius: 0;
    min-height: unset;
    padding: 2rem 0 1rem 0;
    box-sizing: border-box;
    flex-basis: auto;
  }
  .profile-left-inner {
    min-height: unset;
    padding: 0;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
  .profile-right {
    padding: 1.5rem 1rem 1.5rem 1rem;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
    transition: padding 0.2s;
  }
  .profile-title {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
  .profile-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    grid-gap: 6px;
    padding: 2px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .profile-close {
    top: 0.7rem;
    right: 0.7rem;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 1rem;
  }
  .profile-support-buttons {
    flex-direction: row;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
  }
  .profile-support-btn {
    font-size: 1.1rem;
  }
  .profile-delete-link {
    font-size: 0.6rem;
    margin-bottom: 0.7rem;
  }
}
@media (max-width: 600px) {
  .profile-container {
    min-width: 0;
    min-height: 0;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    overflow: auto;
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
  }
  .profile-left {
    padding: 1.2rem 0 0.5rem 0;
  }
  .profile-right {
    padding: 1rem 0.5rem 1rem 0.5rem;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  .profile-title {
    font-size: 1.1rem;
  }
  .profile-grid {
    /* grid-template-columns will be set dynamically via style */
    grid-gap: 4px;
    padding: 1px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .profile-close {
    top: 1.2rem;
    right: 1.2rem;
    width: 2.2rem;
    height: 2.2rem;
    font-size: 1.4rem;
  }
  .profile-support-buttons {
    flex-direction: row;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
  .profile-support-btn {
    font-size: 0.95rem;
    padding: 0.5rem 1.1rem;
    margin-bottom: 0.3rem;
    box-sizing: border-box;
    white-space: nowrap;
  }
  .profile-streaming-label {
    margin-top: 1rem;
    margin-bottom: 0.7rem;
  }
  .profile-support-buttons {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .profile-link {
    margin-bottom: 0.4rem;
  }
}
</style> 